import React, { useState } from "react";
import { func, string } from "prop-types";
import { connect } from "react-redux";
import get from "lodash/get";
import { parseUrl } from "query-string";
import { Link } from "@quintype/components";

import TextField from "../../atoms/TextField";
import Button from "../../atoms/Button";
import MessageView from "../../atoms/MessageView";
import Icons from "../../icons";

import { forgotPassword } from "../../helper/api";
import { isValidEmail, getContinueToText } from "../../utils/utils";
import { setLoading } from "../../helper/actions";

import "./forgotPassword.m.css";

const ForgotPassword = ({ setLoading, authHost, sketchesHost, currentPath }) => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const params = parseUrl(currentPath);
  const redirectUrl = get(params, ["query", "redirect-url"], sketchesHost);
  const callbackUrl = get(params, ["query", "callback-url"], sketchesHost);
  const targetText = getContinueToText(callbackUrl);

  const setData = (e) => {
    setEmail(e.target.value);

    // If error has been set then remove it
    if (e.target.value && emailError) {
      setEmailError("");
    }

    if (e.target.value && error) {
      setError(null);
    }

    if (e.target.value && success) {
      setSuccess(null);
    }
  };

  const onSubmitClick = (event) => {
    event.preventDefault();

    // true -> field errors, false - no fields errors
    if (validate()) {
      return;
    }

    setLoading(true);

    forgotPassword(email.trim(), redirectUrl)
      .then(() =>
        setSuccess(
          `We have sent a reset link via email ${
            email ? `to ${email}` : ""
          }. Please follow the link to reset your password`
        )
      )
      .catch((ex) => {
        if (ex.message) {
          const res = JSON.parse(ex.message);
          setError(res.error.message);
        }
      })
      .finally(() => setLoading(false));
  };

  const validate = () => {
    let flag = false;

    if (!email) {
      setEmailError("Email address is required");
      flag = true;
    } else if (!isValidEmail(email.trim())) {
      setEmailError("Please enter a valid email address");
      flag = true;
    }

    return flag;
  };

  return (
    <div>
      <form onSubmit={onSubmitClick}>
        <div styleName="container">
          <div styleName="wrapper">
            <a aria-label="Back Icon" styleName="back-button" href={callbackUrl}>
              <Icons type="back-icon" height="14px" width="14px" alt="Back Icon" />
              <span styleName="back-text">Back</span>
            </a>
            <div styleName="content">
              <Icons type="palo-bangla" alt="Prothom Alo" className="palo-logo" />
              <h3 className={"forgot-text"}>Forgot Password</h3>
              <div styleName="continue-to">
                <span styleName="continue-to-text">to continue to</span>
                <span styleName="target-site">{targetText}</span>
              </div>
              <div styleName="forgot-pwd-message">
                Enter your account email address and we’ll email you a link so that you can reset your password
              </div>

              <TextField
                value={email}
                error={emailError}
                placeholder="Enter your email address here"
                inputType="type2"
                name="email"
                onChange={setData}
              />

              <MessageView type={error ? "error" : "success"} text={error || success} />

              <Button content="CONTINUE" type="submit" buttonType="type3" />

              <span styleName="new-user">
                New to Prothom Alo?{" "}
                <Link
                  aria-label="New user"
                  href={`authenticate?redirect-url=${redirectUrl}&callback-url=${callbackUrl}&auth-page=${authHost}/sign-up-page`}
                >
                  Create an account
                </Link>
              </span>
            </div>
          </div>

          <div styleName="terms-privacy">
            By proceeding, you agree to{" "}
            <a aria-label="Terms of Use" styleName="terms-privacy-link" href={`${sketchesHost}/terms`}>
              Terms of Use
            </a>{" "}
            and{" "}
            <a aria-label="Privacy Policy" styleName="terms-privacy-link" href={`${sketchesHost}/privacy-policy`}>
              Privacy Policy
            </a>{" "}
            of Prothom Alo
          </div>
        </div>
      </form>
    </div>
  );
};

ForgotPassword.propTypes = {
  setLoading: func,
  authHost: string,
  sketchesHost: string,
  currentPath: string,
};

const mapStateToProps = (state) => ({
  authHost: get(state, ["qt", "config", "publisher-attributes", "auth_host"], null),
  sketchesHost: get(state, ["qt", "config", "sketches-host"], "https://www.prothomalo.com"),
});

const mapDispatchToProps = (dispatch) => ({
  navigateToPage: (url) => global.app.navigateToPage(dispatch, url),
  setLoading: (isLoading) => dispatch(setLoading(isLoading)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ForgotPassword);
